<template>
  <div class="studio-panel studio-panel-right h-full">

    <!-- Panel Header -->
    <div class="panel-header" v-if="!inDrawer">
      <span>{{ $t('Properties') }}</span>
      <el-button v-if="isMobile" circle text @click="$emit('close-mobile')">
        <el-icon><Close /></el-icon>
      </el-button>
    </div>

    <!-- Actions Bar -->
    <div class="p-3 border-b border-gray-100 flex gap-2" data-tour="export">
      <el-button type="primary" class="flex-1" @click="$emit('update-chart')">
         <el-icon class="mr-1"><Refresh /></el-icon> {{ $t('Render') }}
      </el-button>
      <el-button plain class="flex-1" @click="$emit('export-layout')">
         <el-icon class="mr-1"><Download /></el-icon> {{ $t('Export') }}
      </el-button>
    </div>

    <!-- Video/GIF Export Bar -->
    <div class="p-3 border-b border-gray-100 flex gap-2">
      <el-button
        :type="isRecording ? 'danger' : 'default'"
        class="flex-1"
        :loading="isRecording"
        @click="$emit('export-video')"
        :disabled="isRecording"
      >
        <el-icon class="mr-1"><VideoCamera /></el-icon>
        {{ isRecording ? $t('Recording') : $t('Export Video') }}
      </el-button>
      <el-button
        plain
        class="flex-1"
        :loading="isGeneratingGif"
        @click="$emit('export-gif')"
        :disabled="isGeneratingGif"
      >
        <el-icon class="mr-1"><PictureFilled /></el-icon>
        {{ isGeneratingGif ? $t('GeneratingGIF') : $t('Export GIF') }}
      </el-button>
    </div>

    <div class="panel-content custom-scrollbar">

      <!-- Typography -->
      <div class="panel-section">
        <div class="panel-section-title">{{ $t('Typography') }}</div>

        <div class="mb-3" data-tour="font">
           <label class="text-sm font-bold text-gray-900 mb-1.5 block">{{ $t('Font Family') }}</label>
           <el-select
              :model-value="selectedFont"
              @update:model-value="$emit('update:selectedFont', $event)"
              :placeholder="$t('SelectFont')"
              class="w-full"
              size="default"
              filterable
              popper-class="font-preview-popper"
            >
              <el-option
                v-for="font in fontOptions"
                :key="font.value"
                :label="font.label"
                :value="font.value"
              >
                <div class="font-preview-option">
                  <img
                    :src="getFontPreviewUrl(font.value)"
                    :alt="font.label"
                    class="font-preview-img"
                    loading="lazy"
                    @error="handleFontPreviewError($event, font.label)"
                  />
                </div>
              </el-option>
            </el-select>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Appearance -->
      <div class="panel-section">
         <div class="panel-section-title">{{ $t('Appearance') }}</div>

         <!-- Color Picker Row -->
         <div class="flex items-center justify-between mb-3 bg-white p-2 rounded border border-gray-100">
            <span class="text-xs text-gray-600">{{ $t('Background Color') }}</span>
            <el-color-picker
              :model-value="backgroundColor"
              @update:model-value="$emit('update:backgroundColor', $event)"
              show-alpha
              :predefine="predefineColors"
              size="small"
            ></el-color-picker>
         </div>

         <!-- Color Palette Selector -->
         <div class="mb-3" data-tour="colors">
            <label class="text-sm font-bold text-gray-900 mb-1.5 block">{{ $t('Word Colors') }}</label>
            <div class="palette-grid">
              <div
                v-for="palette in colorPalettes"
                :key="palette.name"
                :class="['palette-item', { active: selectedPalette === palette.name }]"
                @click="$emit('update:selectedPalette', palette.name)"
                :title="palette.label"
              >
                <div v-if="palette.colors" class="palette-swatches">
                  <span
                    v-for="(color, ci) in palette.colors.slice(0, 5)"
                    :key="ci"
                    class="palette-swatch"
                    :style="{ backgroundColor: color }"
                  ></span>
                </div>
                <div v-else class="palette-random">
                  <el-icon :size="14"><MagicStick /></el-icon>
                  <span class="text-xs ml-1">{{ palette.label }}</span>
                </div>
              </div>
            </div>
         </div>

         <!-- Background Image -->
         <div class="mb-3" data-tour="background">
            <label class="text-sm font-bold text-gray-900 mb-1.5 block">{{ $t('Background Image') }}</label>
            <div class="flex items-center gap-2 mb-2">
              <el-button size="small" @click="selectBgImage" :type="bgImage ? 'success' : 'primary'" plain>
                <el-icon class="mr-1"><Picture /></el-icon>
                {{ bgImage ? '✓' : '+' }}
              </el-button>
              <input
                ref="bgFileInput"
                type="file"
                accept="image/*"
                @change="handleBgFileSelect"
                style="display: none;"
              >
              <el-button
                v-if="bgImage"
                size="small"
                type="warning"
                plain
                @click="showBgSettings = !showBgSettings"
              >
                <el-icon><Setting /></el-icon>
              </el-button>
              <el-button
                v-if="bgImage"
                size="small"
                type="danger"
                plain
                @click="$emit('clear-bg-image')"
              >
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
            <!-- Background image settings -->
            <div v-if="bgImage && showBgSettings" class="bg-settings-panel p-3 bg-gray-50 rounded border border-gray-100">
              <div class="mb-2">
                <label class="text-xs text-gray-500 mb-1 block">{{ $t('BgOpacity') }}: {{ bgOpacity.toFixed(1) }}</label>
                <el-slider
                  :model-value="bgOpacity"
                  @update:model-value="$emit('update:bgOpacity', $event)"
                  :min="0"
                  :max="1"
                  :step="0.05"
                  size="small"
                />
              </div>
              <div class="mb-2">
                <label class="text-xs text-gray-500 mb-1 block">{{ $t('BgBlur') }}: {{ bgBlur }}px</label>
                <el-slider
                  :model-value="bgBlur"
                  @update:model-value="$emit('update:bgBlur', $event)"
                  :min="0"
                  :max="30"
                  :step="1"
                  size="small"
                />
              </div>
              <div>
                <label class="text-xs text-gray-500 mb-1 block">{{ $t('BgFitMode') }}</label>
                <el-select
                  :model-value="bgFitMode"
                  @update:model-value="$emit('update:bgFitMode', $event)"
                  size="small"
                  class="w-full"
                >
                  <el-option value="cover" :label="$t('BgCover')" />
                  <el-option value="contain" :label="$t('BgContain')" />
                  <el-option value="repeat" :label="$t('BgTile')" />
                  <el-option value="stretch" :label="$t('BgStretch')" />
                </el-select>
              </div>
            </div>
         </div>

         <!-- Mask -->
         <div class="mb-2" data-tour="shape">
            <label class="text-sm font-bold text-gray-900 mb-1.5 block">{{ $t('Shape Mask') }}</label>
            <div class="grid grid-cols-3 gap-2">
               <div
                 class="flex flex-col items-center gap-0.5 py-2 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all text-gray-500"
                 @click="$emit('open-mask-dialog')"
               >
                  <el-icon :size="16"><Grid /></el-icon>
                  <span class="text-[10px] text-gray-400">{{ $t('Templates') }}</span>
               </div>
               <div
                 class="flex flex-col items-center gap-0.5 py-2 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all text-gray-500"
                 @click="selectCustomMask"
               >
                   <el-icon :size="16"><Plus /></el-icon>
                   <span class="text-[10px] text-gray-400">{{ $t('Upload') }}</span>
               </div>
               <div
                 class="flex flex-col items-center gap-0.5 py-2 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all text-gray-500"
                 @click="$emit('open-drawing-board')"
               >
                   <el-icon :size="16"><EditPen /></el-icon>
                   <span class="text-[10px] text-gray-400">{{ $t('Draw') }}</span>
               </div>
            </div>
            <input
              ref="maskFileInput"
              type="file"
              accept="image/*"
              @change="handleMaskFileSelect"
              style="display: none;"
            >
         </div>
      </div>

      <div class="section-divider"></div>

      <!-- Canvas Settings -->
      <div class="panel-section" data-tour="size">
        <div class="panel-section-title">{{ $t('Dimensions') }}</div>

        <el-select
            :model-value="selectedSize"
            @change="$emit('update-image-size', $event)"
            :placeholder="$t('Preset')"
            class="w-full mb-3"
            size="small"
        >
            <el-option
              v-for="size in defaultSizes"
              :key="size.tag"
              :label="size.tag"
              :value="size"
            ></el-option>
        </el-select>

        <div class="flex items-center gap-3">
           <div class="flex-1 flex items-center">
               <span class="text-xs text-gray-400 mr-2 font-mono">W</span>
               <el-input-number
                  :model-value="imageWidth"
                  @update:model-value="$emit('update:imageWidth', $event)"
                  controls-position="right"
                  size="small"
                  class="!w-full"
                  :min="100"
               ></el-input-number>
           </div>

           <div class="flex-1 flex items-center">
               <span class="text-xs text-gray-400 mr-2 font-mono">H</span>
               <el-input-number
                  :model-value="imageHeight"
                  @update:model-value="$emit('update:imageHeight', $event)"
                  controls-position="right"
                  size="small"
                  class="!w-full"
                  :min="100"
               ></el-input-number>
           </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ElButton, ElIcon, ElSelect, ElOption, ElColorPicker, ElInputNumber, ElSlider } from 'element-plus';
import { Close, Refresh, Download, Grid, Plus, Picture, PictureFilled, Setting, VideoCamera, MagicStick, EditPen } from '@element-plus/icons-vue';
import { COLOR_PALETTES } from '@/utils/colorPalettes';

export default {
  name: 'RightSidebar',
  components: {
    ElButton, ElIcon, ElSelect, ElOption, ElColorPicker, ElInputNumber, ElSlider,
    Close, Refresh, Download, Grid, Plus, Picture, PictureFilled, Setting, VideoCamera, MagicStick, EditPen
  },
  props: {
    isMobile: Boolean,
    selectedFont: String,
    fontOptions: Array,
    backgroundColor: String,
    predefineColors: Array,
    selectedSize: Object,
    defaultSizes: Array,
    imageWidth: Number,
    imageHeight: Number,
    inDrawer: Boolean,
    // New props
    selectedPalette: String,
    bgImage: String,
    bgOpacity: Number,
    bgBlur: Number,
    bgFitMode: String,
    isRecording: Boolean,
    isGeneratingGif: Boolean
  },
  emits: [
    'update-chart', 'export-layout', 'open-mask-dialog',
    'mask-file-selected', 'update:selectedFont', 'update:backgroundColor',
    'update-image-size', 'update:imageWidth', 'update:imageHeight', 'close-mobile',
    // New emits
    'update:selectedPalette', 'open-drawing-board',
    'bg-file-selected', 'update:bgOpacity', 'update:bgBlur', 'update:bgFitMode',
    'clear-bg-image', 'export-video', 'export-gif'
  ],
  data() {
    return {
      showBgSettings: false,
      colorPalettes: COLOR_PALETTES
    };
  },
  methods: {
    selectCustomMask() { this.$refs.maskFileInput.click(); },
    handleMaskFileSelect(event) {
      if (event.target.files?.[0]) {
        this.$emit('mask-file-selected', event.target.files[0]);
      }
      event.target.value = '';
    },
    selectBgImage() { this.$refs.bgFileInput.click(); },
    handleBgFileSelect(event) {
      if (event.target.files?.[0]) {
        this.$emit('bg-file-selected', event.target.files[0]);
        this.showBgSettings = true;
      }
      event.target.value = '';
    },
    getFontPreviewUrl(fontValue) {
      const imageName = fontValue.replace(/ /g, '_') + '.png';
      return `/font_previews/${imageName}`;
    },
    handleFontPreviewError(event, fontLabel) {
      const parent = event.target.parentElement;
      if (parent) {
        parent.innerHTML = `<span style="font-family: ${fontLabel}; font-size: 14px;">${fontLabel}</span>`;
      }
    }
  }
}
</script>

<style scoped>
.font-preview-option {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 24px;
  padding: 2px 0;
}
.font-preview-option span {
  font-size: 15px;
  color: var(--color-text-900);
  white-space: nowrap;
}

.font-preview-img {
  height: 24px;
  width: auto;
  object-fit: contain;
}

/* Color Palette Grid */
.palette-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
}

.palette-item {
  cursor: pointer;
  border-radius: 8px;
  border: 2px solid transparent;
  padding: 6px;
  transition: all 0.15s ease;
  background: #fff;
}

.palette-item:hover {
  border-color: #a5b4fc;
  background: #eef2ff;
}

.palette-item.active {
  border-color: #4f46e5;
  background: #eef2ff;
}

.palette-swatches {
  display: flex;
  gap: 2px;
  border-radius: 4px;
  overflow: hidden;
}

.palette-swatch {
  width: 100%;
  height: 18px;
  border-radius: 2px;
}

.palette-random {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 2px;
  color: #6b7280;
  min-height: 30px;
}
.palette-random .el-icon {
  color: #9ca3af;
}

/* Background Image Settings */
.bg-settings-panel {
  animation: slideDown 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>

<style>
/* Global styles for the popper (cannot be scoped) */
.font-preview-popper {
  min-width: 280px !important;
}
.font-preview-popper .el-select-dropdown__item {
  height: auto;
  min-height: 32px;
  padding: 4px 12px;
  line-height: normal;
}
</style>
